import React, { useState, useEffect } from "react";
import { Box, VStack, HStack, Text, Button, Badge, Flex, Tooltip, Select, Icon, Spinner } from "@chakra-ui/react";
import { putScheduledJob, setScheduledJobIsActive } from "services/ScheduleServices";
import cronstrue from "cronstrue";
import showToast from "hooks/useCustomToast";
import { ScheduledJobProto } from "build-ts/schedule";
import { useUser } from "context/UserContext";
import { AppIcons } from "constants/icons";

type ScheduleMode = "minutes" | "hourly" | "daily" | "weekly" | "monthly";

interface CronBuilderProps {
  cronExpression: string;
  setCronExpression: React.Dispatch<React.SetStateAction<string>>;
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  jobDetails: ScheduledJobProto | undefined;
  /** When provided, CronBuilder skips its internal API call and delegates saving to the parent. */
  onSave?: (cronExpr: string) => void;
  /** Opens the execution log modal from the job detail view. */
  onViewExecutionLog?: () => void;
}

const MODES: { value: ScheduleMode; label: string }[] = [
  { value: "minutes", label: "Every N mins" },
  { value: "hourly", label: "Every N hours" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i.toString().padStart(2, "0") + ":00",
}));

const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => ({
  value: m,
  label: m.toString().padStart(2, "0"),
}));

const INTERVAL_MINUTES = [5, 10, 15, 20, 30, 45, 60].map(m => ({
  value: m,
  label: `${m} min`,
}));

const INTERVAL_HOURS = [1, 2, 3, 4, 6, 8, 12].map(h => ({
  value: h,
  label: `${h} hr`,
}));

const WEEKDAYS = [
  { key: "MON", label: "Mon" },
  { key: "TUE", label: "Tue" },
  { key: "WED", label: "Wed" },
  { key: "THU", label: "Thu" },
  { key: "FRI", label: "Fri" },
  { key: "SAT", label: "Sat" },
  { key: "SUN", label: "Sun" },
];

const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

// Quartz day-of-week: 1=SUN, 2=MON, 3=TUE, 4=WED, 5=THU, 6=FRI, 7=SAT
const QUARTZ_NUM_TO_NAME: Record<number, string> = {
  1: "SUN",
  2: "MON",
  3: "TUE",
  4: "WED",
  5: "THU",
  6: "FRI",
  7: "SAT",
};
const DAY_NAME_TO_NUM: Record<string, number> = {
  SUN: 1,
  MON: 2,
  TUE: 3,
  WED: 4,
  THU: 5,
  FRI: 6,
  SAT: 7,
};

const expandDowExpression = (dow: string): string[] => {
  const result: Set<string> = new Set();
  dow.split(",").forEach(token => {
    if (token.includes("-")) {
      const [start, end] = token.split("-");
      const startNum = isNaN(Number(start)) ? DAY_NAME_TO_NUM[start.toUpperCase()] : Number(start);
      const endNum = isNaN(Number(end)) ? DAY_NAME_TO_NUM[end.toUpperCase()] : Number(end);
      if (startNum && endNum) {
        for (let i = startNum; i <= endNum; i++) {
          const name = QUARTZ_NUM_TO_NAME[i];
          if (name) result.add(name);
        }
      }
    } else {
      const asNum = Number(token);
      if (!isNaN(asNum) && QUARTZ_NUM_TO_NAME[asNum]) {
        result.add(QUARTZ_NUM_TO_NAME[asNum]);
      } else {
        const upper = token.toUpperCase();
        if (DAY_NAME_TO_NUM[upper]) result.add(upper);
      }
    }
  });
  return Array.from(result);
};

const buildCron = (
  mode: ScheduleMode,
  intervalMin: number,
  intervalHr: number,
  startHr: number,
  endHr: number | null,
  atHour: number,
  atMinute: number,
  weekdays: string[],
  monthDay: number
): string => {
  switch (mode) {
    case "minutes":
      return `0 0/${intervalMin} * ? * *`;
    case "hourly": {
      const hourRange = endHr !== null ? `${startHr}-${endHr}` : `${startHr}`;
      return `0 0 ${hourRange}/${intervalHr} ? * ${weekdays.length > 0 ? weekdays.join(",") : "*"}`;
    }
    case "daily":
      return `0 ${atMinute} ${atHour} ? * *`;
    case "weekly":
      return `0 ${atMinute} ${atHour} ? * ${weekdays.length > 0 ? weekdays.join(",") : "*"}`;
    case "monthly":
      return `0 ${atMinute} ${atHour} ${monthDay} * ?`;
  }
};

const CronBuilder: React.FC<CronBuilderProps> = ({
  cronExpression,
  setCronExpression,
  isActive,
  setIsActive,
  jobDetails,
  onSave,
  onViewExecutionLog,
}) => {
  const [mode, setMode] = useState<ScheduleMode>("hourly");
  const [intervalMin, setIntervalMin] = useState(30);
  const [intervalHr, setIntervalHr] = useState(6);
  const [startHr, setStartHr] = useState(0);
  const [endHr, setEndHr] = useState<number | null>(null);
  const [atHour, setAtHour] = useState(9);
  const [atMinute, setAtMinute] = useState(0);
  const [weekdays, setWeekdays] = useState<string[]>(["MON"]);
  const [monthDay, setMonthDay] = useState(1);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const { defaultTimeZone } = useUser();
  const hasValidScheduledJob = jobDetails && jobDetails?.header?.id ? true : false;

  useEffect(() => {
    if (jobDetails) setIsActive(jobDetails?.is_active || false);
  }, [jobDetails]);

  useEffect(() => {
    if (!cronExpression) return;
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length < 6) return;

    const [, min, hour, dom, , dow] = parts;

    // Every N minutes: 0 0/N * ? * *
    if (min.includes("/")) {
      const [, n] = min.split("/");
      setMode("minutes");
      setIntervalMin(Number(n) || 30);
      return;
    }

    // Every N hours, optionally between H1-H2: 0 0 H1/N or 0 0 H1-H2/N ? * [days]
    if (hour.includes("/")) {
      const [range, n] = hour.split("/");
      setMode("hourly");
      setIntervalHr(Number(n) || 6);
      if (range.includes("-")) {
        const [h1, h2] = range.split("-");
        setStartHr(Number(h1) || 0);
        setEndHr(Number(h2));
      } else {
        setStartHr(Number(range) || 0);
        setEndHr(null);
      }
      if (dow && dow !== "*" && dow !== "?") {
        setWeekdays(expandDowExpression(dow));
      }
      return;
    }

    // Monthly: 0 M H D * ?
    if (dom !== "?" && dom !== "*" && !Number.isNaN(Number(dom))) {
      setMode("monthly");
      setMonthDay(Number(dom) || 1);
      setAtHour(Number(hour) || 9);
      setAtMinute(Number(min) || 0);
      return;
    }

    // Weekly: day-of-week has specific days
    if (dow && dow !== "*" && dow !== "?") {
      setMode("weekly");
      setWeekdays(expandDowExpression(dow));
      setAtHour(Number(hour) || 9);
      setAtMinute(Number(min) || 0);
      return;
    }

    // Daily fallback
    setMode("daily");
    setAtHour(Number(hour) || 9);
    setAtMinute(Number(min) || 0);
  }, [cronExpression]);

  const builtCron = buildCron(mode, intervalMin, intervalHr, startHr, endHr, atHour, atMinute, weekdays, monthDay);

  const toggleWeekday = (day: string) => {
    setWeekdays(prev =>
      prev.includes(day)
        ? prev.length === 1
          ? prev // keep at least one selected
          : prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const getCronDescription = (expr: string): string => {
    try {
      return cronstrue.toString(expr);
    } catch {
      return expr;
    }
  };

  const handleSave = async () => {
    if (onSave) {
      setCronExpression(builtCron);
      setIsEditMode(false);
      onSave(builtCron);
      return;
    }
    if (jobDetails?.header?.id) {
      const job = ScheduledJobProto.create({
        header: jobDetails.header,
        cron_expr: builtCron,
        type: jobDetails.type,
        is_active: isActive,
        scheduled_analysis: jobDetails.scheduled_analysis,
      });
      const response = await putScheduledJob(job);
      if (response) {
        showToast("Schedule saved", "", "success");
        setCronExpression(response.cron_expr || "");
        setIsEditMode(false);
      } else {
        showToast("Failed to save schedule", "", "error");
      }
    } else {
      setCronExpression(builtCron);
      setIsEditMode(false);
    }
  };

  const handleToggle = async () => {
    if (!hasValidScheduledJob) return;
    const next = !isActive;
    setIsToggling(true);
    try {
      const ok = await setScheduledJobIsActive(jobDetails?.header?.id || "", next);
      if (ok) {
        showToast(`Schedule ${next ? "activated" : "deactivated"}`, "", "success");
        setIsActive(next);
      } else {
        showToast("Failed to toggle status", "", "error");
      }
    } catch {
      showToast("Failed to toggle status", "", "error");
    } finally {
      setIsToggling(false);
    }
  };

  const selectStyle = {
    size: "xs" as const,
    bg: "white",
    borderColor: "gray.200",
    borderRadius: "md",
    fontSize: "xs",
    h: "28px",
    _focus: { borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" },
  };

  const labelStyle = {
    fontSize: "xs" as const,
    color: "gray.500",
    whiteSpace: "nowrap" as const,
  };

  const renderControls = () => {
    switch (mode) {
      case "minutes":
        return (
          <HStack spacing={2} align="center">
            <Text {...labelStyle} mb={0}>
              Every
            </Text>
            <Select
              {...selectStyle}
              w="100px"
              value={intervalMin}
              onChange={e => setIntervalMin(Number(e.target.value))}
              fontWeight="600"
            >
              {INTERVAL_MINUTES.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </HStack>
        );

      case "hourly":
        return (
          <HStack spacing={2} align="center" flexWrap="wrap">
            <Text {...labelStyle} mb={0}>
              Every
            </Text>
            <Select
              {...selectStyle}
              w="90px"
              value={intervalHr}
              onChange={e => setIntervalHr(Number(e.target.value))}
              fontWeight="600"
            >
              {INTERVAL_HOURS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Text {...labelStyle} mb={0}>
              from
            </Text>
            <Select
              {...selectStyle}
              w="85px"
              value={startHr}
              onChange={e => setStartHr(Number(e.target.value))}
              fontWeight="600"
              color={startHr === 0 ? "gray.400" : "gray.700"}
            >
              {HOURS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Select
              {...selectStyle}
              w="90px"
              value={endHr ?? ""}
              onChange={e => setEndHr(e.target.value === "" ? null : Number(e.target.value))}
              color={endHr === null ? "gray.400" : "gray.700"}
              fontWeight="600"
            >
              <option value="">no end</option>
              {HOURS.map(o => (
                <option key={o.value} value={o.value}>{`until ${o.label}`}</option>
              ))}
            </Select>
            <Box w="1px" h="16px" bg="gray.200" mx={1} flexShrink={0} />
            {WEEKDAYS.map(d => {
              const active = weekdays.includes(d.key);
              return (
                <Button
                  key={d.key}
                  size="xs"
                  h="22px"
                  minW="30px"
                  px={1}
                  fontSize="10px"
                  fontWeight={active ? "600" : "400"}
                  borderRadius="md"
                  border="1px"
                  borderColor={active ? "purple.400" : "gray.200"}
                  bg={active ? "purple.50" : "white"}
                  color={active ? "purple.700" : "gray.400"}
                  _hover={{ bg: active ? "purple.100" : "gray.50" }}
                  onClick={() => toggleWeekday(d.key)}
                >
                  {d.label}
                </Button>
              );
            })}
          </HStack>
        );

      case "daily":
      case "weekly":
        return (
          <HStack spacing={2} align="center" flexWrap="wrap">
            {mode === "weekly" && (
              <HStack spacing="3px" mr={1}>
                {WEEKDAYS.map(d => {
                  const active = weekdays.includes(d.key);
                  return (
                    <Button
                      key={d.key}
                      size="xs"
                      h="22px"
                      minW="30px"
                      px={1}
                      fontSize="10px"
                      fontWeight={active ? "600" : "400"}
                      borderRadius="md"
                      border="1px"
                      borderColor={active ? "purple.400" : "gray.200"}
                      bg={active ? "purple.50" : "white"}
                      color={active ? "purple.700" : "gray.400"}
                      _hover={{ bg: active ? "purple.100" : "gray.50" }}
                      onClick={() => toggleWeekday(d.key)}
                    >
                      {d.label}
                    </Button>
                  );
                })}
                <Box w="1px" h="16px" bg="gray.200" mx={1} flexShrink={0} />
              </HStack>
            )}
            <Text {...labelStyle} mb={0}>
              at
            </Text>
            <Select
              {...selectStyle}
              w="85px"
              value={atHour}
              onChange={e => setAtHour(Number(e.target.value))}
              fontWeight="600"
            >
              {HOURS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.400" mb={0}>
              :
            </Text>
            <Select
              {...selectStyle}
              w="72px"
              value={atMinute}
              onChange={e => setAtMinute(Number(e.target.value))}
              fontWeight="600"
            >
              {MINUTES.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </HStack>
        );

      case "monthly":
        return (
          <HStack spacing={2} align="center" flexWrap="wrap">
            <Text {...labelStyle} mb={0}>
              On day
            </Text>
            <Select
              {...selectStyle}
              w="72px"
              value={monthDay}
              onChange={e => setMonthDay(Number(e.target.value))}
              fontWeight="600"
            >
              {MONTH_DAYS.map(d => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
            <Text {...labelStyle} mb={0}>
              at
            </Text>
            <Select
              {...selectStyle}
              w="85px"
              value={atHour}
              onChange={e => setAtHour(Number(e.target.value))}
              fontWeight="600"
            >
              {HOURS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.400" mb={0}>
              :
            </Text>
            <Select
              {...selectStyle}
              w="72px"
              value={atMinute}
              onChange={e => setAtMinute(Number(e.target.value))}
              fontWeight="600"
            >
              {MINUTES.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </HStack>
        );
    }
  };

  const canToggle = hasValidScheduledJob && !isToggling ? true : false;
  const cronDescriptionText = cronExpression ? getCronDescription(cronExpression) : "";

  return (
    <Box px={2} py={2}>
      <VStack spacing={3} align="stretch">
        {/* Single header row — label + preview + status toggle */}
        <Flex align="center" gap={3} minH="28px">
          {isEditMode && (
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" flexShrink={0} mb={0}>
              Set a schedule
            </Text>
          )}

          {!isEditMode &&
            (cronExpression ? (
              <Tooltip label={cronDescriptionText} placement="top" hasArrow openDelay={300}>
                <Flex
                  align="center"
                  gap={2}
                  display="inline-flex"
                  px={3}
                  py={1}
                  minH="28px"
                  maxW="280px"
                  minW={0}
                  flexShrink={0}
                  overflow="hidden"
                  cursor="pointer"
                  onClick={() => setIsEditMode(true)}
                  transition="all 0.15s"
                  border="2px solid"
                  borderColor="purple.700"
                  borderRadius="md"
                  bg="white"
                  color="purple.700"
                  fontWeight="700"
                  fontSize="xs"
                  _hover={{ bg: "purple.100", borderColor: "purple.600" }}
                >
                  <Text fontSize="xs" fontWeight="700" color="purple.700" noOfLines={1} mb={0} minW={0} flex="1">
                    {cronDescriptionText}
                  </Text>
                  <Badge
                    colorScheme="purple"
                    fontSize="2xs"
                    variant="subtle"
                    flexShrink={0}
                    bg="purple.50"
                    px={1.5}
                    py={0.5}
                    lineHeight="1"
                    borderRadius="sm"
                  >
                    {defaultTimeZone}
                  </Badge>
                </Flex>
              </Tooltip>
            ) : (
              <Button
                size="xs"
                variant="ghost"
                color="gray.400"
                fontWeight="normal"
                onClick={() => setIsEditMode(true)}
              >
                + Set schedule
              </Button>
            ))}

          {onViewExecutionLog && (
            <Button
              size="xs"
              variant="outline"
              flexShrink={0}
              minH="28px"
              h="auto"
              py={1}
              px={3}
              border="2px solid"
              borderColor="purple.700"
              borderRadius="md"
              bg="purple.50"
              color="purple.700"
              fontWeight="700"
              fontSize="xs"
              _hover={{ bg: "purple.100", borderColor: "purple.600" }}
              onClick={onViewExecutionLog}
            >
              View execution log
            </Button>
          )}

          {/* Clickable status pill — replaces the old toggle+switch */}
          {isToggling ? (
            <HStack
              spacing={2}
              bg={isActive ? "green.50" : "gray.50"}
              borderRadius="md"
              px={3}
              py={1}
              border="1px"
              borderColor={isActive ? "green.400" : "gray.300"}
              ml="auto"
              flexShrink={0}
              minW="72px"
              justify="center"
            >
              <Spinner size="xs" color={isActive ? "green.500" : "gray.400"} />
            </HStack>
          ) : (
            <Box ml="auto" flexShrink={0}>
              <Tooltip
                label={canToggle ? (isActive ? "Click to pause" : "Click to activate") : undefined}
                placement="top"
                hasArrow
                shouldWrapChildren
              >
                <HStack
                  spacing={2}
                  bg={isActive ? "green.50" : "gray.50"}
                  borderRadius="md"
                  px={3}
                  py={1}
                  border="1px"
                  borderColor={isActive ? "green.400" : "gray.300"}
                  cursor={canToggle ? "pointer" : "default"}
                  onClick={canToggle ? handleToggle : undefined}
                  _hover={canToggle ? { opacity: 0.75 } : {}}
                  transition="all 0.15s"
                  minW="72px"
                  justify="center"
                >
                  {isActive ? (
                    <Box w="8px" h="8px" borderRadius="full" bg="green.400" flexShrink={0} />
                  ) : (
                    <Icon as={AppIcons.pause} boxSize={3} color="gray.400" />
                  )}
                  <Text fontSize="sm" fontWeight="700" color={isActive ? "green.700" : "gray.500"} mb={0}>
                    {isActive ? "LIVE" : "Paused"}
                  </Text>
                </HStack>
              </Tooltip>
            </Box>
          )}
        </Flex>

        {isEditMode && (
          /* ── Edit mode ── */
          <VStack spacing={2} align="stretch" bg="white" border="1px" borderColor="gray.400" borderRadius="lg" p={3}>
            {/* Preview — top */}
            <Flex align="center" gap={2} px={2} py={2} borderRadius="md" border="2px" borderColor="purple.500">
              <Text fontSize="xs" fontWeight="700" color="purple.700" flex={1} noOfLines={1} mb={0} textAlign="left">
                {getCronDescription(builtCron)}
              </Text>
              <Badge colorScheme="purple" fontSize="xs" variant="subtle" flexShrink={0} bg="purple.50">
                {defaultTimeZone}
              </Badge>
            </Flex>

            {/* Mode + Controls inline */}
            <Flex align="center" gap={2} minH="40px">
              <Select
                {...selectStyle}
                w="130px"
                flexShrink={0}
                value={mode}
                onChange={e => setMode(e.target.value as ScheduleMode)}
                color="gray.700"
                _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
                fontWeight="600"
              >
                {MODES.map(m => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
              <Box w="1px" h="20px" bg="gray.200" flexShrink={0} />
              {renderControls()}
            </Flex>

            {/* Actions */}
            <HStack spacing={2} justify="flex-start" pt={1}>
              <Button
                variant="ghost"
                size="xs"
                color="gray.400"
                onClick={() => {
                  setIsEditMode(false);
                }}
              >
                Cancel
              </Button>
              <Button colorScheme="purple" size="xs" onClick={handleSave} px={4}>
                Save
              </Button>
            </HStack>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default CronBuilder;
